import React, { useState, ReactNode, useCallback } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { Locale } from 'date-fns/locale';
import { useUniqueId, useInterval, useForceUpdate, useRequestLanguage } from '@/utils/hooks';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';
import DateTimeDisplay from './DateTimeDisplay';
import { Tooltip } from '@/components/Tooltip';

type IOwnProps = {
    value: Date | string;
    prefix?: ReactNode;
    suffix?: ReactNode;
    placement?: 'top' | 'bottom' | 'left' | 'right' | undefined;
    disableTooltip?: boolean;
};

function DateDistanceDisplay({ value, prefix, suffix, placement = 'top', disableTooltip = false }: IOwnProps) {
    const uniqueId = useUniqueId();

    const [locale, setLocale] = useState<Locale | null>(null);

    useRequestLanguage(useCallback((name, obj) => setLocale(obj), []));
    const forceUpdate = useForceUpdate();

    useInterval(() => {
        forceUpdate();
    }, 5000);

    if (!value) {
        return null;
    }

    if (!locale) {
        return null;
    }

    const content = (
        <span id={uniqueId}>
            {prefix}
            {formatDistanceToNow(parseISODate(value), { addSuffix: true, locale })}
            {suffix}
        </span>
    );

    if (disableTooltip) {
        return content;
    }

    return (
        <Tooltip placement={placement} content={<DateTimeDisplay value={parseISODate(value)} />} interactive={false}>
            {content}
        </Tooltip>
    );
}

export default React.memo(DateDistanceDisplay);